// MFA challenge UI used during sign-in. After email/password succeeds,
// Firebase throws `auth/multi-factor-auth-required` and we get a resolver
// that lists the user's enrolled factors. The user picks one (or we default
// to the first), enters their code, and we resolve the sign-in.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  TotpMultiFactorGenerator,
  type MultiFactorResolver,
} from 'firebase/auth';
import { auth } from '../../firebase';
import { authErrorMessage } from '../../lib/auth-errors';

export default function MfaChallengeStep({
  resolver,
  onResolved,
  onCancel,
}: {
  resolver: MultiFactorResolver;
  onResolved: () => void;
  onCancel: () => void;
}) {
  // Default to the first enrolled factor — most users will only have one.
  const [hintIndex, setHintIndex] = useState(0);
  const hint = resolver.hints[hintIndex];
  const isTotp = hint.factorId === TotpMultiFactorGenerator.FACTOR_ID;

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState('');
  const [smsVerificationId, setSmsVerificationId] = useState<string | null>(null);
  const recaptchaContainerRef = useRef<HTMLDivElement | null>(null);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);

  // Reset when switching factors.
  useEffect(() => {
    setCode('');
    setError(null);
    setSmsVerificationId(null);
  }, [hintIndex]);

  // Set up the invisible reCAPTCHA once. SMS challenge needs it; TOTP doesn't,
  // but creating it eagerly keeps the JSX simpler and the cost is negligible.
  useEffect(() => {
    if (!recaptchaContainerRef.current) return;
    const verifier = new RecaptchaVerifier(auth, recaptchaContainerRef.current, {
      size: 'invisible',
    });
    recaptchaVerifierRef.current = verifier;
    return () => {
      verifier.clear();
      recaptchaVerifierRef.current = null;
    };
  }, []);

  // For SMS, the user has to request a code first. TOTP uses the existing
  // app-generated code, so the input is shown immediately.
  const phoneSendNeeded = !isTotp && !smsVerificationId;

  const factorOptions = useMemo(
    () =>
      resolver.hints.map((h, i) => ({
        index: i,
        label: h.displayName ?? (h.factorId === TotpMultiFactorGenerator.FACTOR_ID ? 'Authenticator app' : 'SMS'),
      })),
    [resolver.hints],
  );

  async function requestSmsCode() {
    if (!recaptchaVerifierRef.current || busy) return;
    setBusy(true);
    setError(null);
    try {
      const provider = new PhoneAuthProvider(auth);
      const id = await provider.verifyPhoneNumber(
        { multiFactorHint: hint, session: resolver.session },
        recaptchaVerifierRef.current,
      );
      setSmsVerificationId(id);
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (isTotp) {
        const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code.trim());
        await resolver.resolveSignIn(assertion);
      } else {
        if (!smsVerificationId) throw new Error('SMS code not requested');
        const cred = PhoneAuthProvider.credential(smsVerificationId, code.trim());
        const assertion = PhoneMultiFactorGenerator.assertion(cred);
        await resolver.resolveSignIn(assertion);
      }
      onResolved();
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h2>Two-factor auth</h2>
      <p style={{ color: 'var(--muted)' }}>
        Enter the code from your {isTotp ? 'authenticator app' : 'SMS'} to finish signing in.
      </p>

      {factorOptions.length > 1 && (
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
            Use a different method:
          </label>
          <select
            value={hintIndex}
            onChange={(e) => setHintIndex(Number(e.target.value))}
            disabled={busy}
            style={{ marginLeft: '0.5rem' }}
          >
            {factorOptions.map((opt) => (
              <option key={opt.index} value={opt.index}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {error && <p style={{ color: 'salmon' }}>{error}</p>}

      {phoneSendNeeded ? (
        <button type="button" onClick={requestSmsCode} disabled={busy}>
          {busy ? 'Sending…' : 'Send SMS code'}
        </button>
      ) : (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            placeholder="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            autoFocus
          />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" onClick={onCancel} disabled={busy}>
              Cancel
            </button>
            <button type="submit" disabled={busy || code.length !== 6}>
              {busy ? 'Verifying…' : 'Verify'}
            </button>
          </div>
        </form>
      )}

      <div ref={recaptchaContainerRef} />
    </div>
  );
}
